import { isTimeSlot, type ShiftWithAdjustments } from "@src/appV2/Shifts/types";

import {
  applyReplacementPairStates,
  computeShiftState,
  getComputedArrivalForWorker,
} from "./computeShiftState";
import { getTimeSlotFromDateString } from "./groupShiftsByTimeSlot";
import { type SentHomeInfo } from "./parseSentHome";
import {
  type PendingInviteInfo,
  SHIFT_STATES,
  type ShiftCardData,
  STATE_PRIORITY,
} from "./shiftCardTypes";

// FIXME: Automatically disabled during ESLint config update
/* eslint-disable no-barrel-files/no-barrel-files */
export {
  applyReplacementPairStates,
  computeShiftState,
  getComputedArrivalForWorker,
  getSentHomeInfoFromShift,
} from "./computeShiftState";
export {
  type PendingInviteInfo,
  type ProfileImageSizes,
  SHIFT_STATES,
  type ShiftCardData,
  type ShiftState,
} from "./shiftCardTypes";
/* eslint-enable no-barrel-files/no-barrel-files */

interface MapShiftToCardParams {
  shift: ShiftWithAdjustments;
  sentHomeMap: Map<string, SentHomeInfo>;
  workerNamesMap?: Map<string, string>;
  workerProfileImagesMap?: Map<string, string>;
  timezone?: string;
  pendingInvitesMap?: Map<string, PendingInviteInfo>;
}

function normalizeTimestamp(timestamp: string): string {
  return timestamp.replace(/\.\d{3}Z$/, "Z");
}

export function getShiftSlotKey(start: string, end: string, agentRequest: string): string {
  return `${normalizeTimestamp(start)}|${normalizeTimestamp(end)}|${agentRequest}`;
}

export function getPostedQualificationName(shift: ShiftWithAdjustments): string {
  return shift.originalReq ?? shift.agentReq;
}

export function mapShiftToCard(params: MapShiftToCardParams): ShiftCardData {
  const {
    shift,
    sentHomeMap,
    workerNamesMap,
    workerProfileImagesMap,
    timezone,
    pendingInvitesMap,
  } = params;

  const sentHomeInfo = sentHomeMap.get(shift._id);
  const computedArrival = getComputedArrivalForWorker(shift);
  let state = computeShiftState(shift, sentHomeInfo, computedArrival);

  const slotKey = getShiftSlotKey(shift.start, shift.end, shift.agentReq);
  const pendingInvite = pendingInvitesMap?.get(slotKey);

  if (state === SHIFT_STATES.UNFILLED && pendingInvite) {
    state = SHIFT_STATES.INVITE_SENT;
  }

  const timeSlot =
    shift.name && isTimeSlot(shift.name)
      ? shift.name
      : getTimeSlotFromDateString(shift.start, timezone);

  const workerId = shift.agent?.userId;

  return {
    shiftId: shift._id,
    state,
    agentReq: shift.agentReq,
    postedQualificationName: getPostedQualificationName(shift),
    startTime: shift.start,
    endTime: shift.end,
    timeSlot,
    workerName: workerId ? workerNamesMap?.get(workerId) : undefined,
    workerId,
    profileImageUrl: workerId ? workerProfileImagesMap?.get(workerId) : undefined,
    profileImageSizes: shift.agent?.profileImageSizes,
    clockInTime: shift.clockInOut?.start,
    clockOutTime: shift.clockInOut?.end,
    clockInPreregisteredAt: shift.clockInOut?.clockInPreregisteredAt,
    hasUrgency: shift.urgency !== undefined,
    urgencyLevel: shift.urgency,
    replacedShiftId: shift.replacedShiftId ?? undefined,
    sentHomeInfo,
    geofenceEntryTime: shift.attendance?.arrivedFacilityGeofenceAt?.toISOString(),
    geofenceExitTime: shift.attendance?.leftFacilityGeofenceAt?.toISOString(),
    hasLeftGeofence: shift.attendance?.leftFacilityGeofence,
    isWorkerFarFromFacility: shift.attendance?.isWorkerFarFromFacility,
    workerLeftEarlyInfo: shift.workerLeftEarly
      ? {
          leftWithPermission: shift.workerLeftEarly.leftWithPermission,
          replacementRequested: shift.workerLeftEarly.replacementRequested,
        }
      : undefined,
    previousWorker: shift.previouslyAssignedWorker
      ? {
          ...shift.previouslyAssignedWorker,
          profileImageUrl: workerProfileImagesMap?.get(shift.previouslyAssignedWorker.userId),
        }
      : undefined,
    extendBookabilityTo: shift.extendBookabilityTo,
    computedArrival,
    pendingInvite,
    createdAt: shift.createdAt,
    deleted: shift.deleted,
    deletedAt: shift.cancelledAt,
    postedByName: shift.postedBy?.name,
    isBillable: shift.isBillable,
    shiftSpecifications: shift.shiftSpecifications,
    genderRequirement: shift.genderRequirement,
    pricingSettings: shift.pricingSettings,
  };
}

export function mapShiftsToCards(params: {
  shifts: readonly ShiftWithAdjustments[];
  sentHomeMap: Map<string, SentHomeInfo>;
  workerNamesMap?: Map<string, string>;
  workerProfileImagesMap?: Map<string, string>;
  timezone?: string;
  pendingInvitesMap?: Map<string, PendingInviteInfo>;
}): ShiftCardData[] {
  const {
    shifts,
    sentHomeMap,
    workerNamesMap,
    workerProfileImagesMap,
    timezone,
    pendingInvitesMap,
  } = params;

  const cards = shifts.map((shift) =>
    mapShiftToCard({
      shift,
      sentHomeMap,
      workerNamesMap,
      workerProfileImagesMap,
      timezone,
      pendingInvitesMap,
    })
  );

  applyReplacementPairStates(cards);

  return cards.sort((a, b) => STATE_PRIORITY[a.state] - STATE_PRIORITY[b.state]);
}
